import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Search, Home, Users, Gift, MapPin, Utensils, Check, Star, Megaphone, Building2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { JobCard } from "../components/JobCard";
import { DownloadApp } from "../components/DownloadApp";
import { getShowHomeStats } from "../lib/settings";
import { AdSlot } from "../components/AdSense";

const HERO_IMG =
  "https://images.unsplash.com/photo-1577219492769-b63a779fac28?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const AD_IMG =
  "https://images.pexels.com/photos/9870223/pexels-photo-9870223.jpeg?auto=compress&cs=tinysrgb&w=400";

const WhyCard = ({ icon: Icon, title, text, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4 }}
    className="flex gap-4 bg-white rounded-2xl border border-stone-border shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
    data-testid={testid}
  >
    <div className="w-12 h-12 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
      <Icon className="w-6 h-6 text-brand" />
    </div>
    <div>
      <h3 className="font-heading text-lg font-bold text-stone-text">{title}</h3>
      <p className="text-sm text-stone-sub mt-1 leading-relaxed">{text}</p>
    </div>
  </motion.div>
);

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data || [])).catch(() => {});
    api.get("/jobs/stats").then(({ data }) => setStats(data)).catch(() => {});
    getShowHomeStats().then(setShowStats);
  }, []);

  const totalJobs = stats?.active ?? jobs.length;
  const housingCount = stats?.with_housing ?? jobs.filter((j) => j.offers_housing).length;
  const featured = [...jobs]
    .sort((a, b) => (b.offers_housing === a.offers_housing ? 0 : b.offers_housing ? 1 : -1))
    .slice(0, 6);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/jobs${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  const whyCards = [
    { icon: MapPin, title: t("home.why.nearTitle"), text: t("home.why.nearText"), testid: "why-near" },
    { icon: Home, title: t("home.why.housingTitle"), text: t("home.why.housingText"), testid: "why-housing" },
    { icon: Users, title: t("home.why.staffTitle"), text: t("home.why.staffText"), testid: "why-staff" },
    { icon: Gift, title: t("home.why.trialTitle"), text: t("home.why.trialText"), testid: "why-trial" },
  ];

  const planCards = [
    { key: "free", name: t("home.plansData.freeName"), price: t("home.plansData.freePrice"), showMo: false, features: t("home.plansData.freeFeatures", { returnObjects: true }), featured: false },
    { key: "basic", name: t("home.plansData.basicName"), price: t("home.plansData.basicPrice"), showMo: true, features: t("home.plansData.basicFeatures", { returnObjects: true }), featured: true },
    { key: "advanced", name: t("home.plansData.advancedName"), price: t("home.plansData.advancedPrice"), showMo: true, features: t("home.plansData.advancedFeatures", { returnObjects: true }), featured: false },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-text via-stone-text/85 to-stone-text/45" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-3">
              <Utensils className="w-9 h-9 text-brand" />
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight" data-testid="hero-title">
                {t("home.heroTitle")}
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-white/90 max-w-xl leading-relaxed">{t("home.heroSubtitle")}</p>

            <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2" data-testid="hero-search-form">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-muted" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("jobs.searchPlaceholder")}
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white text-stone-text placeholder:text-stone-muted focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <button
                data-testid="hero-search-btn"
                type="submit"
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors"
              >
                <Search className="w-5 h-5" /> {t("home.browse")}
              </button>
            </form>

            {!user && (
              <div className="mt-3 grid grid-cols-2 gap-2 max-w-md">
                <Link
                  to="/login"
                  data-testid="hero-login-btn"
                  className="text-center py-3 rounded-xl bg-white/95 text-brand font-semibold border-2 border-white hover:bg-white transition-colors"
                >
                  {t("home.login")}
                </Link>
                <Link
                  to="/register"
                  data-testid="hero-signup-btn"
                  className="text-center py-3 rounded-xl bg-brand-soft text-stone-text font-semibold hover:bg-white transition-colors"
                >
                  {t("home.signup")}
                </Link>
              </div>
            )}

            {showStats && (
              <div className="flex flex-wrap gap-6 mt-8" data-testid="hero-stats">
                <div>
                  <div className="font-heading text-3xl font-bold text-white">{totalJobs}</div>
                  <div className="text-sm text-white/70">{t("hero.stat_jobs")}</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-bold text-housing flex items-center gap-1">
                    <Home className="w-6 h-6" /> {housingCount}
                  </div>
                  <div className="text-sm text-white/70">{t("hero.stat_housing")}</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-bold text-white">{stats?.without_housing ?? Math.max(0, totalJobs - housingCount)}</div>
                  <div className="text-sm text-white/70">{t("hero.stat_noHousing")}</div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Why RestauranteJobs */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-text mb-6">{t("home.whyTitle")}</h2>
        <div className="grid grid-cols-1 gap-4">
          {whyCards.map((c) => <WhyCard key={c.testid} {...c} />)}
        </div>

        {/* Sponsored */}
        <a
          href="#"
          data-testid="sponsored-card"
          className="mt-4 flex items-center gap-4 bg-white rounded-2xl border-2 border-brand/40 shadow-sm p-4 hover:shadow-md transition-all"
        >
          <img src={AD_IMG} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
          <div className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-brand">
              <Megaphone className="w-3.5 h-3.5" /> {t("home.sponsored")}
            </span>
            <h3 className="font-heading text-lg font-bold text-stone-text leading-tight">{t("home.adTitle")}</h3>
            <p className="text-sm text-stone-sub">{t("home.adSubtitle")}</p>
          </div>
        </a>

        <AdSlot className="mt-6" />
      </section>

      {/* Plans for employers */}
      <section className="bg-stone-bg py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-text">{t("home.plansTitle")}</h2>
          <p className="text-stone-sub mt-1 mb-6">{t("home.plansSubtitle")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {planCards.map((p) => (
              <div
                key={p.key}
                data-testid={`home-plan-${p.key}`}
                className={`relative bg-white rounded-2xl border-2 shadow-sm p-6 flex flex-col ${p.featured ? "border-brand" : "border-stone-border"}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 right-6 flex items-center gap-1 text-xs font-bold bg-brand-soft text-brand px-3 py-1 rounded-full">
                    <Star className="w-3 h-3 fill-brand" /> {t("home.popular")}
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold text-stone-text">{p.name}</h3>
                <div className="mt-2 mb-4">
                  <span className="font-heading text-3xl font-bold text-brand">{p.price}</span>
                  {p.showMo && <span className="text-stone-muted">{t("home.perMonth")}</span>}
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {(Array.isArray(p.features) ? p.features : []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-sub">
                      <Check className="w-4 h-4 text-housing mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-center">
            <Link
              to="/register"
              data-testid="home-register-btn"
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors"
            >
              <Building2 className="w-5 h-5" /> {t("home.signup")}
            </Link>
          </div>
        </div>
      </section>

      {/* Featured jobs */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-text">{t("home.featuredTitle")}</h2>
            <Link to="/jobs" data-testid="landing-view-all" className="text-sm font-semibold text-brand hover:underline">
              {t("home.browse")} →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((job) => <JobCard key={job.id} job={job} />)}
          </div>
        </section>
      )}

      {/* Download app CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-brand-soft border border-stone-border p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-bold text-stone-text">{t("download.title")}</h3>
            <p className="text-stone-sub mt-1">{t("download.subtitle")}</p>
          </div>
          <DownloadApp variant="landing" />
        </div>
      </section>
    </div>
  );
}
